import { Loading } from "../../app/Status";
import { useQuery } from "@tanstack/react-query";
import {
  CONDITION_SCORE_LABELS,
  DEFECT_SEVERITY_LABELS,
  type AssetConditionDto,
  type ConditionScore,
} from "@klantpunt/shared";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";

/**
 * Blok *Conditie* bij een asset (docs/CONDITIEMETING.md §10.5): laatste gemeten conditie, de
 * theoretische conditie van vandaag met de verbruikte levensduur, en het verloop over metingen.
 * Alleen met recht op de module Conditiemeting.
 */
const fmtDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });

export function ConditionSection({ assetId }: { assetId: string }) {
  const { data: me } = useMe();
  const see = hasModule(me, "condition");
  const q = useQuery({
    queryKey: ["asset-condition", assetId],
    queryFn: () => api<AssetConditionDto>(`/assets/${assetId}/condition`),
    enabled: see,
  });
  if (!see) return null;
  const c = q.data;
  const last = c?.history[0];
  return (
    <section className="sec" aria-label="Conditie">
      <p className="label">Conditie (NEN 2767)</p>
      {!c ? (
        <Loading small />
      ) : (
        <>
          <div className="row">
            <span className={`condscore cond-${c.score ?? "none"}`} aria-hidden="true">
              {c.score ?? "?"}
            </span>
            <p className="small grow">
              {c.score != null ? (
                <>
                  Gemeten conditie <strong>{c.score}</strong> ({CONDITION_SCORE_LABELS[c.score as ConditionScore]})
                  {c.assessedOn && ` · ${fmtDate(c.assessedOn)}`}
                  {last?.provisional && <span className="muted"> · voorlopige score</span>}
                </>
              ) : (
                <span className="muted">Nog niet gemeten.</span>
              )}
              <span className="block">
                {c.theoreticalToday != null ? (
                  <>
                    Theoretisch vandaag <strong>{c.theoreticalToday}</strong> ·{" "}
                    {Math.round((c.ageRatioToday ?? 0) * 100)}% van de levensduur verbruikt
                  </>
                ) : (
                  <span className="muted">Theoretisch: {c.missing.join(" en ")}</span>
                )}
              </span>
            </p>
          </div>
          {c.history.length > 0 && (
            <ul className="list compact" aria-label="Verloop">
              {c.history.map((h) => (
                <li key={h.assessmentId} className="small">
                  {h.assessedOn ? fmtDate(h.assessedOn) : "—"}:{" "}
                  {h.notInspectable ? "niet te inspecteren" : `conditie ${h.effectiveScore ?? "—"}`}
                  {h.theoreticalScore != null && ` (theoretisch ${h.theoreticalScore})`}
                  {h.defects.length > 0 &&
                    ` · ${h.defects.map((d) => `${d.title} (${DEFECT_SEVERITY_LABELS[d.severity].toLowerCase()})`).join(", ")}`}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
